import React, { useEffect, useCallback, useState, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import updateDocumentTitle from '../../utils/updateDocumentTitle';
import Sidebar from './ReportComponents/Sidebar';
import PrimaryNavigation from '../../components/header/PrimaryNavigation/index';
import SecondaryNavigation from './ReportComponents/SecondaryNavigation';
import A4 from './A4Pages';
import SectionTemplate from './A4Pages/SectionTemplate';
import TableOfContents from './A4Pages/TableOfContents';
import ReportCoverPage from './ReportComponents/ReportCoverPage';
import Image from './getDataFromImage';
import {
  ReportDetailContent,
  ImageSet,
  Photonotes,
  MarkerTable,
  InfoTable,
  Notes,
} from './ReportComponents/ReportImageSection';
import './printStyles.css';
import './styles.css';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/configureStore';
import {
  deleteSelectedImageFromReport,
  setActiveDraftReport,
  storeReportInfo,
} from '../../store/slices/reportSlice';

import { imageArchive } from '@fluke/baseline-client-apis';
import { LoadingOverlay } from '../../components/Overlays';
import { UserContext } from '../../context/UserProvider';
import routerPathEnum from '../../enums/routerPathEnum';
import { setSelectedImageIds } from '../../store/slices/imagesSlice';
import { deleteFromReportData } from '../../utils/reports';
import snackBarStatusEnum from '../../enums/snackBarStatusEnum';
import AlertSnackBar from '../../components/utility/AlertSnackBar';
import { useHistory } from 'react-router-dom';

function checkImagesLoaded(images) {
  if (!Array.isArray(images)) {
    // If `images` is not an array, convert it to a single-element array
    images = [images];
  }

  for (var image of images) {
    const { preview, visual, url } = image || {};

    if (url != null) {
      // if the url doesn't exist, skip
      if (!preview && !visual) {
        // at least one image has not loaded. Break.
        return false;
      }
    } else {
      console.warn('Image skipped. No URL provided.');
    }
  }
  // all images have loaded
  return true;
}

async function getReportImages(reportInfo) {
  const { image_urls = [] } = reportInfo || {};

  try {
    const images = await Promise.all(
      image_urls.map(({ url }) => imageArchive.fetchImageArchiveByUrl(url)),
    );

    return images || [];
  } catch (error) {
    console.warn(error);
  }
}

export default function ReportDetail({ props }) {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { userSettings: { temperatureUnits = 'F' } = {} } = useContext(UserContext);

  const { id } = useParams();
  updateDocumentTitle(props.location.pathname);

  const { reportInfo } = useSelector((state) => state.reportData);

  const [scale, setScale] = useState(1);
  const [reportImages, setReportImages] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState(snackBarStatusEnum.DEFAULT);

  const imageData = (imageId = '') => {
    if (!reportImages || reportImages?.length === 0) return;

    const image = reportImages.find((img) => img.imageId === imageId);

    const { photonotes, preview, visual, imageJSON } = image || {};
    const { main, marker_info, image_info, notes } = imageJSON || {};

    const markers = Image.getMarkers(marker_info, temperatureUnits);
    const info = Image.getInfo(image_info, main?.user_timezone, temperatureUnits);
    const noteEntries = Image.getNotes(notes);
    const details = Image.getDetails(imageJSON, reportInfo);

    return {
      imageId,
      name: main?.origin_path,
      details,
      images: [preview, visual],
      markers,
      info,
      photonotes,
      notes: noteEntries,
    };
  };

  const metadata = useMemo(() => {
    const { data = {}, company_logo } = reportInfo || {};
    return { ...data, company_logo };
  }, [reportInfo?.data, reportInfo?.company_logo]);

  const documentSections = useMemo(
    () => reportInfo?.images?.map(imageData).filter(Boolean) || [],
    [reportInfo?.images, reportImages, temperatureUnits],
  );

  const handleNavClick = (id) => {
    const scrollableRegion = document.getElementById('reportview-canvas');
    const element = document.getElementById(`${id}`);
    if (element) {
      const offsetTop = element.offsetTop * scale; // multiply by the document scale factor
      if (scrollableRegion) {
        scrollableRegion.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        });
      } else {
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        });
      }
    }
  };

  const handleImageEdit = (imageId) => {
    dispatch(setSelectedImageIds([imageId]));
    history.push(routerPathEnum.IMAGE_EDITOR);
  };

  const handleImageDelete = useCallback(
    async (imageId) => {
      if (!reportInfo) {
        return; // Return early if reportInfo is null or undefined
      }

      const updatedReportData = deleteFromReportData(reportInfo, imageId);
      dispatch(deleteSelectedImageFromReport(imageId));
      const response = await dispatch(storeReportInfo(updatedReportData));

      setSnackbar((prevSnackbar) => {
        if (response && response.isAxiosError) {
          return snackBarStatusEnum.ERROR;
        } else if (prevSnackbar === snackBarStatusEnum.ERROR) {
          return prevSnackbar; // Keep the error status if it's already set
        } else {
          return snackBarStatusEnum.IMAGE_DELETED; // Set the deleted status
        }
      });
    },
    [dispatch, reportInfo],
  );

  useEffect(() => {
    dispatch(setActiveDraftReport(id));
  }, [id]);

  useEffect(() => {
    const loadImages = async () => {
      const newReportImages = await getReportImages(reportInfo);
      setReportImages(newReportImages);
    };
    loadImages();
  }, [reportInfo]);

  useEffect(() => {
    // check if current report is the active report
    if (id === reportInfo?.options?.id) {
      // check if all report images have loaded
      const isDone = checkImagesLoaded(reportImages);
      setIsLoading(isDone === false);
    }
  }, [id, reportInfo, reportImages]);

  return (
    <>
      <LoadingOverlay open={isLoading} overlayStyle={{ zIndex: '100' }} />
      <AlertSnackBar alert={snackbar} setAlert={setSnackbar} />

      <div className="reportview-container" id="reportDetail">
        <PrimaryNavigation />
        <SecondaryNavigation />
        <div className="reportview-main">
          <Sidebar reportInfo={reportInfo} onClick={handleNavClick} onDelete={handleImageDelete} />

          <A4.Document id="reportview-canvas" scale={scale} setScale={setScale}>
            <SectionTemplate id="reportCoverPage" name="Cover Page" hasHeader={false}>
              <A4.Section id="reportCoverPage">
                <ReportCoverPage reportInfo={reportInfo} />
              </A4.Section>
            </SectionTemplate>

            {reportInfo?.images?.length > 0 && (
              <TableOfContents id="tableOfContents" name="Table of Contents" metadata={metadata} />
            )}

            {documentSections.map(
              ({ imageId, name, details, images, info, markers, photonotes = [], notes = [] }) => (
                <SectionTemplate key={imageId} id={imageId} name={`${name}`} metadata={metadata}>
                  <ReportDetailContent
                    details={details}
                    disabled={reportInfo?.options?.finalized}
                  />

                  <ImageSet
                    name="Thermal Image"
                    imageId={imageId}
                    images={images}
                    disabled={reportInfo?.options?.finalized}
                    onEdit={handleImageEdit}
                    onDelete={handleImageDelete}
                  />

                  <div name="Info Tables" className="reportsection-row">
                    <MarkerTable markers={markers} />
                    <InfoTable info={info} />
                  </div>
                  {photonotes.length > 0 && <Photonotes name="Photonotes" images={photonotes} />}
                  {notes.length > 0 && <Notes name="Notes" notes={notes} />}
                </SectionTemplate>
              ),
            )}
          </A4.Document>
        </div>
      </div>
    </>
  );
}
